import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const TechnicalDocumentationContainer = styled.div`
  padding: 140px 0;
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #2B4B8C, #3B82C4);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 500px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1541746972996-4e0b0f93e586?ixlib=rb-4.0.3&auto=format&fit=crop&w=2126&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(43, 75, 140, 0.85), rgba(59, 130, 196, 0.7));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  
  h2 {
    font-size: 3.5rem;
    
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  }
  
  p {
    font-size: 1.3rem;
    
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.95;
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Documents Section
const DocumentsSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .docs-grid .doc-card:nth-child(1) { animation: ${slideInLeft} 0.6s ease-out 0.1s both; }
    .docs-grid .doc-card:nth-child(2) { animation: ${slideInLeft} 0.6s ease-out 0.2s both; }
    .docs-grid .doc-card:nth-child(3) { animation: ${slideInLeft} 0.6s ease-out 0.3s both; }
    .docs-grid .doc-card:nth-child(4) { animation: ${slideInLeft} 0.6s ease-out 0.4s both; }
    .docs-grid .doc-card:nth-child(5) { animation: ${slideInLeft} 0.6s ease-out 0.5s both; }
    .docs-grid .doc-card:nth-child(6) { animation: ${slideInLeft} 0.6s ease-out 0.6s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const DocsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DocCard = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 20px;
  border: 1px solid rgba(43, 75, 140, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #2B4B8C, #3B82C4);
    transform: scaleY(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(43, 75, 140, 0.12);
    border-color: rgba(43, 75, 140, 0.15);
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  .doc-icon {
    font-size: 3rem;
    color: #2B4B8C;
    margin-bottom: 25px;
    transition: all 0.4s ease;
  }
  
  &:hover .doc-icon {
    transform: scale(1.1);
    color: #3B82C4;
  }
  
  h3 {
    font-size: 1.6rem;
    color: #2c2c2c;
    margin-bottom: 15px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.6;
    font-size: 1rem;
    
    margin-bottom: 25px;
  }
  
  .download-btn {
    background: linear-gradient(135deg, #2B4B8C, #3B82C4);
    color: white;
    border: none;
    padding: 12px 25px;
    border-radius: 25px;
    font-weight: 500;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(43, 75, 140, 0.3);
    }
    
    .icon {
      font-size: 1rem;
    }
  }
`;

// Categories Section
const CategoriesSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 80px 0;
  border-radius: 30px;
  margin-bottom: 120px;
  
  &.animate-in {
    .categories-grid .category-card:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .categories-grid .category-card:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .categories-grid .category-card:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
    .categories-grid .category-card:nth-child(4) { animation: ${scaleIn} 0.6s ease-out 0.7s both; }
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.div`
  background: white;
  padding: 30px 20px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  .category-icon {
    font-size: 2.5rem;
    color: #2B4B8C;
    margin-bottom: 15px;
    transition: all 0.4s ease;
  }
  
  &:hover .category-icon {
    transform: scale(1.1);
    color: #3B82C4;
  }
  
  h3 {
    font-size: 1.2rem;
    color: #2c2c2c;
    margin-bottom: 10px;
    font-weight: 500;
  }
  
  .count {
    color: #6c757d;
    font-size: 0.9rem;
    
  }
`;

// Contact Section
const ContactSection = styled.section`
  background: linear-gradient(135deg, #2B4B8C 0%, #3B82C4 100%);
  padding: 100px 0;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  h2 {
    font-size: 3rem;
    
    color: white;
    margin-bottom: 25px;
    letter-spacing: -1px;
  }
  
  p {
    font-size: 1.3rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto 40px;
    
  }
  
  .contact-btn {
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid white;
    color: white;
    padding: 15px 40px;
    border-radius: 30px;
    font-weight: 500;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;
    backdrop-filter: blur(10px);
    letter-spacing: 0.5px;
    
    &:hover {
      background: white;
      color: #2B4B8C;
      transform: translateY(-3px);
    }
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.2rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const TechnicalDocumentation = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleDownload = (docType) => {
    // Bu normalde gerçek PDF dosyasına yönlendirirdi
    alert(`${docType} dokümanı indiriliyor... (Demo)`);
  };

  return (
    <TechnicalDocumentationContainer>
      <Container>
        <PageTitle>{t('technicalDocumentation.title')}</PageTitle>
        <PageSubtitle>
          {t('technicalDocumentation.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('technicalDocumentation.heroTitle')}</h2>
            <p>
              {t('technicalDocumentation.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <DocumentsSection 
          id="docs-section" 
          data-animate
          className={animatedSections.has('docs-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('technicalDocumentation.docsTitle')}</SectionTitle>
          
          <div style={{ textAlign: "center", padding: "80px 20px", background: "#f8fafc", borderRadius: "20px", border: "1px dashed #cbd5e1", maxWidth: "800px", margin: "0 auto" }}>
            <div style={{ fontSize: "3rem", marginBottom: "20px" }}>⏳</div>
            <h3 style={{ fontSize: "1.8rem", color: "#1e293b", marginBottom: "15px", fontWeight: "500" }}>{t('technicalDocumentation.comingSoonTitle', 'Çok Yakında!')}</h3>
            <p style={{ fontSize: "1.1rem", color: "#64748b" }}>{t('technicalDocumentation.comingSoonDesc', 'Belgelerimiz ve sertifikalarımız çok yakında eklenecektir.')}</p>
          </div>

        </DocumentsSection>

        

        <ContactSection 
          id="contact-section" 
          data-animate
          className={animatedSections.has('contact-section') ? 'animate-in' : ''}
        >
          <h2>{t('technicalDocumentation.infoTitle')}</h2>
          <p>
            {t('technicalDocumentation.infoDesc')}
          </p>
          <a href="/iletisim" className="contact-btn">
            {t('technicalDocumentation.contactBtn')}
          </a>
        </ContactSection>
      </Container>
    </TechnicalDocumentationContainer>
  );
};

export default TechnicalDocumentation;
